import { useApi, useSavings } from '@/api';
import {
  Banner,
  KpiCard,
  KpiEntityName,
  Link,
  Page,
  PageSkeleton
} from '@/components';
import SavingsIcon from '@/components/icons/SavingsIcon';
import { useSearchParams } from '@/hooks';
import { tokensMapping } from '@/utils/tokens';
import {
  Box,
  CryptoIcon,
  Flex,
  Heading,
  LinkWrapper,
  PercentageValue
} from '@blockanalitica/ui';
import HistoricTabs from './HistoricTabs';

export default function SavingsPage() {
  const [daysAgo] = useSearchParams('days_ago', '1');
  const [data, error] = useApi({
    url: '/save/',
    queryParams: {
      days_ago: daysAgo
    }
  });

  const [savings, savingsError] = useSavings(daysAgo);

  if (error) {
    throw error;
  }
  if (savingsError) {
    throw savingsError;
  }

  if (data && savings) {
    return (
      <Page
        title={
          <Flex gap="2xs" alignItems="center">
            <SavingsIcon size="5" />
            <Heading size="1">Savings</Heading>
          </Flex>
        }>
        <Banner>
          <Box paddingTop="xs" paddingBottom="m" maxWidth="85%">
            <Heading size="2">About the savings rate</Heading>
            <br />
            <b>Dai Savings Rate (DSR):</b>
            A variable, on-chain rate applied to DAI deposited into the DaI
            Savings Rate contract. Accrual is measured in DAI and follows
            governance determined rates and may change over time; amounts are
            not guaranteed. Current display is annualized.
            <br />
            <br />
            <b>Sky Savings Rate (SSR):</b>A variable, on-chain rate applied to
            USDS deposited into the SSR contract. Accrual is measured in USDS
            and follows governance determined rates and may change over time;
            amounts are not guaranteed. Current display is annualized.
          </Box>
        </Banner>

        <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
          <Flex flexBasis="49.5%">
            <KpiCard
              title="Total Savings Supply"
              variant="usds"
              field="total"
              data={savings}
              options={{
                prefixIcon: (
                  <KpiEntityName
                    symbol2={
                      <CryptoIcon
                        name="DAI"
                        address={tokensMapping('DAI').address}
                        size="3"
                      />
                    }
                    symbol1={
                      <CryptoIcon
                        name="USDS"
                        address={tokensMapping('USDS').address}
                        size="3"
                      />
                    }></KpiEntityName>
                )
              }}
            />
          </Flex>

          <KpiCard
            title="Savings Rate"
            data={data}
            field="rate"
            options={{
              percentage: true,
              variants: { valuePercentageValue: 'dynamic' }
            }}
          />
          <KpiCard
            title="Utilization"
            data={savings}
            field="utilization"
            options={{
              percentage: true,
              variants: { valuePercentageValue: 'dynamic' }
            }}
          />
        </Flex>

        <HistoricTabs />
        <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
          <Flex flexBasis="50%">
            <LinkWrapper
              to="sky-savings-rate"
              // @ts-ignore
              as={Link}
              variant="kpiCardLinkWithBlendMode">
              <KpiCard
                variant="usds"
                title="Sky Savings Rate"
                data={savings}
                field="total_susds"
                options={{
                  linkArrow: true,
                  currency: tokensMapping('USDS')
                }}
                footer={
                  <Flex gap="3xs">
                    <PercentageValue
                      value={savings.total_susds / savings.total}
                    />{' '}
                    of total savings supply
                  </Flex>
                }
              />
            </LinkWrapper>
          </Flex>
          <Flex flexBasis="50%">
            <LinkWrapper
              to="dai-savings-rate"
              // @ts-ignore
              as={Link}
              variant="kpiCardLinkWithBlendMode">
              <KpiCard
                variant="dai"
                title="DAI Savings Rate"
                data={savings}
                field="total_dsr"
                options={{
                  linkArrow: true,
                  currency: tokensMapping('DAI')
                }}
                footer={
                  <Flex gap="3xs">
                    <PercentageValue
                      value={savings.total_dsr / savings.total}
                    />{' '}
                    of total savings supply
                  </Flex>
                }
              />
            </LinkWrapper>
          </Flex>
        </Flex>
      </Page>
    );
  }
  return <PageSkeleton />;
}
